import { useState, type FormEvent } from "react";
import { Icon } from "../lib/Icon";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../lib/toast";
import { ApiError } from "../api/client";
import { LocationFields } from "../lib/LocationFields";

interface Props {
  onClose: () => void;
}

const COLOR_PALETTE = [
  "#10b981", "#2563eb", "#dc2626", "#f59e0b", "#ec4899",
  "#7c3aed", "#06b6d4", "#84cc16", "#f97316", "#1e293b",
];

export function EditProfileModal({ onClose }: Props) {
  const { user, updateMe } = useAuth();
  const toast = useToast();

  const [name, setName] = useState(user?.name || "");
  const [handle, setHandle] = useState(user?.handle || "");
  const [country, setCountry] = useState(user?.country || "Colombia");
  const [city, setCity] = useState(user?.city || "");
  const [region, setRegion] = useState(user?.region || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [color, setColor] = useState(user?.avatar_color || "#10b981");
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateMe({
        name: name.trim() || undefined,
        handle: handle.trim() || undefined,
        country,
        city: city.trim(),
        region: region.trim(),
        bio: bio.trim(),
        avatar_color: color,
      });
      toast("Perfil actualizado");
      onClose();
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 520, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: 0 }}>Editar perfil</h2>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>

        <form onSubmit={submit} className="col" style={{ gap: 14 }}>
          <div className="field">
            <label>Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field">
            <label>Handle (opcional)</label>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="@tucuenta"
            />
            <div className="hint">Cómo te identifican otros usuarios. Único en la plataforma.</div>
          </div>

          <LocationFields
            country={country}
            city={city}
            region={region}
            onChange={({ country: c, city: ci, region: r }) => {
              setCountry(c); setCity(ci); setRegion(r);
            }}
            disabled={saving}
          />

          <div className="field">
            <label>Bio (opcional)</label>
            <textarea
              rows={3}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Cuenta algo de ti como coleccionista…"
              maxLength={500}
            />
            <div className="hint">{bio.length}/500</div>
          </div>

          <div className="field">
            <label>Color de avatar</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{
                    width: 36, height: 36, borderRadius: "50%",
                    border: color === c ? "3px solid var(--fg)" : "2px solid var(--border)",
                    background: c, cursor: "pointer", padding: 0,
                  }}
                  aria-label={`Color ${c}`}
                />
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
